import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Page, Group, Layout, Button, Grid, ExcelUpload, FormControl, Table, ErrorUnitProps } from "@/comn/components";
import {
    useForm,
    useToast,
    useFetch,
    useModal,
    useAuth,
    useStore,
    useGrid,
    usePopup,
    useResource,
    useExcel,
    usePage,
} from "@/comn/hooks";
import { BASE, URLS, APIS, SF_LPCO_INFO_U, SG_LPCO_ATCD_FILE_LIST } from "./services/OsciCfgnAtcmService";
import { CommonErrors } from "@/comn/components";
import { lpcoUtils } from "@/tz/go/egaz/osci/lpco/comn";

/*
 * @ 화면 컴포넌트 주석
 * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
 * @ !== ... ==! 치환 부호는 개발 완료 후 한글 주석을 인지하여 제거하기 위한 표시
 * @ 기본 여러줄의 주석 형태를 사용
 * @ 포맷
 * @ [화면명 영문]
 * @ [지환부호시작] [화면명 한글] [치환부호종료]
 *
 * 화면명 영문
 * !== 화면명 한글 ==!
 */

/*
 * Register LPCO information
 * !== LPCO 정보 등록 ==!
 */
export const OSCI0203017S = () => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0004"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    //    const { dclrNo } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const { openPopup } = usePopup();
    const [appAtcmList, setAppAtcmList] = useState(comnUtils.getGridData([]));

    // ORG LPCO LIST
    const [orgLpcoList, setOrgLpcoList] = useState([]);

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0025" },
            { area: "comnCd", comnCd: "OSC0113" },
            { area: "comnCd", comnCd: "OSC0114" },
            { area: "comnCd", comnCd: "OSC0117" },
            { area: "comnCd", comnCd: "OSC0125" },
            { area: "comnCd", comnCd: "OSC0126" },
            { area: "comnCd", comnCd: "OSC0100" },
            { area: "comnCd", comnCd: "OSC0113" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Register LPCO information !== LPCO 정보 등록 ==!
        lpcoInfoRgsr: useForm({
            defaultSchema: SF_LPCO_INFO_U,
            defaultValues: { appGnrlTp: "GE", orgCd: auth.get("orgCd"), orgNm: auth.get("orgNm") },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Attachments List !== 첨부파일 목록 ==!
        appAtcmList: useGrid({
            defaultSchema: SG_LPCO_ATCD_FILE_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        saveAppAtcmList: useFetch({
            api: (data) => APIS.saveAppAtcmList({ ...data }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006" /**Processed successfully. */,
                    onConfirm: () => {
                        navigate(URLS.osci0203020s + "/" + data.appSaveInfo?.content?.appFmId);
                    },
                    onCancel: () => {
                        navigate(URLS.osci0203020s + "/" + data.appSaveInfo?.content?.appFmId);
                    },
                });
            },
            onError: (error) => {
                console.log(error);
                if (error.response.status === 400) {
                    console.log(error.response.data);
                    form.lpcoInfoRgsr.setErrors(error.response.data.errors);
                }

                const errMsg = error?.response?.data?.errorMessage || "msg.osc.00015";
                modal.openModal({
                    content: errMsg,
                });
            },
        }),
        getOrgLpcoByPrcsList: useFetch({
            api: (data) => {
                return lpcoUtils.getOrgLpcoByPrcsList(data);
            },
            onSuccess: (data) => {
                console.log("getOrgLpcoByPrcsList onSuccess ", data);
                setOrgLpcoList(data || []);
            },
            onError: () => {},
            showToast: false,
        }),
        getAppDtl: useFetch({
            api: (appCdNo) => {
                return APIS.getAppDtlByAppCdNo(appCdNo);
            },
            onSuccess: (data) => {
                console.log("getOrgLpcoByPrcsList onSuccess ", data);
                console.log("getAppDtl onSuccess ==================== ", data);
                form.lpcoInfoRgsr.setValues(data.appInfo.content?.info);
                setAppAtcmList(comnUtils.getGridData(data.appInfo.content?.atcmList || []));

                console.log("form.lpcoInfoRgsr ", form.lpcoInfoRgsr.getValues());
            },
            onError: () => {},
            showToast: false,
        }),
    };
    /*
     * 화면에서 사용하는 이벤트, 함수를 선언
     * 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * fetch 를 호출 하는 메소드: fetch 명과 동일하게 작성
     * 버튼이나 그리드의 이벤트의 경우 : [이벤트종류]_[이벤트대상타입]_[이벤트대상명]
     * 이벤트 대상 타입 : Btn , Grid ... 카멜표기법으로 표시, 첫글자 대문자
     * 이벤트 명 : 카멜표기법으로 표시, 첫글자 대문자
     *
     * const handler = {
     *      [handler 이름] : ()=>{ 함수 },
     *      click_Btn_Save : 저장버튼 클릭
     *      click_Grid_RpckItmAppList : 재포장 품목 신청서 그리드 클릭
     *      ...
     * }
     */
    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Save Attachments !== 첨부파일 저장 ==!
        saveAppAtcmList: form.lpcoInfoRgsr.handleSubmit(
            () => {
                // grid validate
                let errResult = grid.appAtcmList.validate() || {};
                errResult["errors"] = errResult?.errors || [];

                // Duplicate Check
                const appAtcmList = grid.appAtcmList.getViewData();
                const atcmTpCdMap: { [key: string]: boolean } = {};
                let idx = 1;
                for (const item of appAtcmList) {
                    const atcmTpCd = item.atcmTpCd;
                    if (atcmTpCd && atcmTpCdMap[atcmTpCd]) {
                        errResult.errors.push(lpcoUtils.getErrorObj(idx, "L_ATCM_NM", "msg.osc.00017", atcmTpCd));
                    } else {
                        atcmTpCdMap[atcmTpCd] = true;
                    }
                    idx++;
                }
                if (errResult && errResult.errors?.length > 0) {
                    // sort by row
                    errResult.errors = lpcoUtils.errorListSortByRow(errResult.errors);
                    // error layer popup
                    modal.openModal({
                        content: <CommonErrors {...errResult} />,
                        size: "lg",
                        draggable: true,
                    });
                } else {
                    const data = { ...form.lpcoInfoRgsr.getValues(), atcmList: grid.appAtcmList.getData() };
                    modal.openModal({
                        content: "msg.osc.00090",
                        onConfirm: () => {
                            fetch.saveAppAtcmList.fetch(data);
                        },
                    });
                }
            },
            // () => {
            //     toast.showToast({ type: "warning", content: "msg.osc.00003" });
            // },
        ),
        getAppDtl: (appCdNo: any) => {
            console.log("getAppDtl ", appCdNo);
            fetch.getAppDtl.fetch(appCdNo);
        },
        click_Btn_AddGridList: () => {
            form.lpcoInfoRgsr.handleSubmit(() => {
                grid.appAtcmList.addRow({ rqrdInpYn: "N" });
            })();
        },
        click_Btn_DeleteGridList: () => {
            const seltList: any[] = grid.appAtcmList.getChecked() || [];
            if (comnUtils.isEmpty(seltList)) {
                modal.openModal({ content: "msg.osc.00009" });
                return;
            }

            grid.appAtcmList.deleteRow("checkbox");

            // modal.openModal({
            //     content: "msg.osc.00095",
            //     onConfirm: () => {
            //         grid.appAtcmList.deleteRow("checkbox");
            //     },
            // });
        },
        setInit: async () => {
            // 기관 LPCO SELECT 목록
            fetch.getOrgLpcoByPrcsList.fetch(auth.get("orgCd"));
        },
    };

    useEffect(() => {
        handler.setInit();
    }, []);

    return (
        <Page
        // id={pgeUid}
        // title={t("T_ATCM_RGSR")}
        // navigation={{
        //     base: comnEnvs.base,
        //     nodes: [...BASE.nodes, { path: "/lpco/cfgn/atcm/osci0203017s", label: "T_ATCM_RGSR" }],
        // }}
        >
            <Group>
                <Group.Body>
                    <Group.Title title={"T_LPCO_APP_INFO"} titleSize={2}></Group.Title>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.lpcoInfoRgsr.schema.orgNm}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.lpcoInfoRgsr.schema.appCdNo}
                                options={orgLpcoList}
                                onChange={(ev: any) => {
                                    console.log("change ev : ", ev);
                                    if (ev) {
                                        handler.getAppDtl(ev);
                                    } else {
                                        grid.appAtcmList.clearData();
                                        form.lpcoInfoRgsr.setValue("appTpCd", "");
                                    }
                                }}
                            ></Group.Control>
                            <Group.Control {...form.lpcoInfoRgsr.schema.appTpCd}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_ATCM_LST_1"} titleSize={2}></Group.Title>
                    <Layout direction="row">
                        <Layout.Left></Layout.Left>
                        <Layout.Right>
                            <Button
                                role="gridAdd"
                                onClick={() => {
                                    handler.click_Btn_AddGridList();
                                }}
                            ></Button>
                            <Button
                                role="gridDelete"
                                onClick={() => {
                                    handler.click_Btn_DeleteGridList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                    {/*
                     * 그리드
                     * @ 그리드 스키마 주입 : {...grid.[그리드이름].grid}
                     * @ 데이터 data={fetch.[fetch 명].data?.[api 리턴 vo 명]}
                     * @ 셀클릭이벤트 연결 : onCellClick={handler.[그리드 이벤트 핸들러명]}
                     */}
                    <Grid
                        {...grid.appAtcmList.grid}
                        data={appAtcmList} /* fetch.getRpckItmAppList.data?.appAtcmList */
                        // render={ render.grid_RpckItmAppList}
                        // onCellClick={handler.click_Grid_RpckItmAppList}
                        // onRowClick={handler.click_GridRow_RpckItmAppList}
                    />
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0203018q);
                        }}
                    ></Button>
                    <Button
                        role="reset"
                        onClick={() => {
                            form.lpcoInfoRgsr.reset({ ...lpcoUtils.orgInfo });
                            grid.appAtcmList.clearData();
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button role="save" onClick={handler.saveAppAtcmList}></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
